import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { FaBolt, FaMoneyBillWave } from "react-icons/fa";
import { FaLeaf } from "react-icons/fa6";
import { useLocation } from "react-router-dom";

const CaseStudyPage = () => {
  const location = useLocation();

  // 🔥 SCROLL TO SECTION WHEN LINK HAS #
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 200);
      }
    }
  }, [location]);

  return (
    <div className="bg-white">
      <Navbar />

      {/* ================= HERO ================= */}
      <section className="relative h-[70vh] flex items-center justify-center text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{
            backgroundImage: "url('/Assets/solarblog.jpg')",
          }}
        />

        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center max-w-3xl px-6"
        >
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
            6.2kW Solar Installation in Lagos
          </h1>
          <p className="mt-4 text-white/80 text-lg">
            A complete solar solution powering a hotel with efficiency,
            reliability, and long-term savings.
          </p>
        </motion.div>
      </section>

      {/* ================= OVERVIEW ================= */}
      <section
        id="overview"
        className="py-20 px-6 max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center"
      >
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          <h2 className="text-3xl font-bold text-gray-900">System Overview</h2>

          <p className="text-gray-600 leading-relaxed">
            This installation features a 6.2kW hybrid solar inverter paired with
            a 15kWh lithium battery and 24 high-efficiency solar panels,
            delivering reliable energy for a hotel in Lagos.
          </p>

          <ul className="space-y-3 text-gray-700">
            <li>✔ 6.2kW Hybrid Inverter (MPPT enabled)</li>
            <li>✔ 15kWh Lithium Battery Storage</li>
            <li>✔ 24 × 350W Solar Panels</li>
          </ul>
        </motion.div>

        <motion.img
          initial={{ opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          src="/Assets/installation1.jpeg"
          className="rounded-2xl shadow-2xl"
        />
      </section>

      {/* ================= PROCESS ================= */}
      <section id="process" className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.img
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            src="/Assets/installation2.jpeg"
            className="rounded-2xl shadow-2xl"
          />

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-bold text-gray-900">
              Installation Process
            </h2>

            <p className="text-gray-600 leading-relaxed">
              The system was carefully installed to ensure optimal efficiency,
              safety, and long-term durability. Every component was configured
              to maximize solar generation and battery performance.
            </p>

            <ul className="space-y-3 text-gray-700">
              <li>✔ Site inspection & load analysis</li>
              <li>✔ Panel mounting & wiring</li>
              <li>✔ Inverter & battery configuration</li>
              <li>✔ System testing & optimization</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ================= RESULT ================= */}
      <section
        id="result"
        className="py-20 px-6 max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center"
      >
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          <h2 className="text-3xl font-bold text-gray-900">
            Final Result & Impact
          </h2>

          <p className="text-gray-600 leading-relaxed">
            The completed system now powers essential operations within the
            hotel, significantly reducing reliance on grid electricity and
            diesel generators.
          </p>

          <ul className="space-y-3 text-gray-700">
            <li className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition">
              <FaBolt className="text-yellow-500" />
              Up to 40kWh daily energy production
            </li>

            <li className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition">
              <FaMoneyBillWave className="text-green-600" />
              Millions saved over 15 years
            </li>

            <li className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition">
              <FaLeaf className="text-green-500" />
              Reduced carbon footprint
            </li>
          </ul>
        </motion.div>

        <motion.img
          initial={{ opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          src="/Assets/installation4.jpeg"
          className="rounded-2xl shadow-2xl"
        />
      </section>

      {/* ================= CTA ================= */}
      <section className="bg-gradient-to-b from-white to-green-50 text-gray-900 py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold">
            Want a Similar Installation?
          </h2>
          <p className="mt-4 text-black/80">
            Let’s design a solar system tailored to your needs.
          </p>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudyPage;
